import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FaPhone, FaEnvelope } from 'react-icons/fa';
import { API_BASE } from '../../config/api';
import { formatDateOnly } from '../../utils/dateOnly';
import './AdminBookings.css';

const STATUS_OPTIONS = [
  { value: 'not_paid', label: 'غير مدفوع', color: '#dc3545' },
  { value: 'paid', label: 'مدفوع', color: '#28a745' },
  { value: 'onhold', label: 'معلق', color: '#ffc107' },
  { value: 'done', label: 'منجز', color: '#17a2b8' },
  { value: 'ended', label: 'منتهي', color: '#6c757d' },
  { value: 'closed', label: 'مغلق', color: '#343a40' },
];

const EMPTY_FILTERS = {
  date: '',
  time: '',
  guest: '',
  phone: '',
  service: '',
  price: '',
  status: '',
};

const includesIgnoreCase = (value, query) => {
  if (!query) return true;
  return String(value ?? '')
    .toLowerCase()
    .includes(query.trim().toLowerCase());
};

const formatDate = (dateStr) => formatDateOnly(dateStr);

const getStatusInfo = (status) => {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
};

const getBookingStatus = (booking) => booking.status || 'not_paid';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      console.error('No admin token found');
      setLoading(false);
      return;
    }

    axios
      .get(`${API_BASE}/api/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setBookings(res.data.bookings || []);
      })
      .catch((err) => {
        console.error('Error fetching bookings:', err);
        setBookings([]);
      })
      .finally(() => setLoading(false));
  };

  const updateStatus = async (bookingId, newStatus) => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setUpdating(bookingId);
    try {
      await axios.patch(
        `${API_BASE}/api/admin/bookings/${bookingId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: newStatus } : b))
      );
    } catch (err) {
      console.error('Status update error:', err);
      alert(err.response?.data?.error || 'فشل تحديث الحالة');
    } finally {
      setUpdating(null);
    }
  };

  const activeBookings = useMemo(
    () => bookings.filter((b) => getBookingStatus(b) !== 'closed'),
    [bookings]
  );

  const closedBookings = useMemo(
    () => bookings.filter((b) => getBookingStatus(b) === 'closed'),
    [bookings]
  );

  const tabBookings = activeTab === 'closed' ? closedBookings : activeBookings;

  const statusFilterOptions =
    activeTab === 'closed'
      ? STATUS_OPTIONS.filter((s) => s.value === 'closed')
      : STATUS_OPTIONS.filter((s) => s.value !== 'closed');

  const visibleBookings = useMemo(() => {
    return tabBookings.filter((b) => {
      const status = getBookingStatus(b);
      const formattedDate = formatDate(b.booking_date);
      const rawDate = String(b.booking_date ?? '');
      const service = b.service_header || b.service_name || '';
      const priceLabel = b.price != null && b.price !== '' ? String(b.price) : '';

      const dateOk =
        includesIgnoreCase(formattedDate, filters.date) ||
        includesIgnoreCase(rawDate, filters.date);
      const guestOk =
        includesIgnoreCase(b.guest_name, filters.guest) ||
        includesIgnoreCase(b.guest_email, filters.guest);

      return (
        dateOk &&
        includesIgnoreCase(b.slot_time, filters.time) &&
        guestOk &&
        includesIgnoreCase(b.guest_phone, filters.phone) &&
        includesIgnoreCase(service, filters.service) &&
        includesIgnoreCase(priceLabel, filters.price) &&
        (!filters.status || status === filters.status)
      );
    });
  }, [tabBookings, filters]);

  const hasActiveFilters = Object.values(filters).some((v) => v.trim() !== '');

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilters((prev) => ({ ...prev, status: '' }));
  };

  if (loading) return <div className="admin-loading">جاري التحميل...</div>;

  return (
    <div className="admin-bookings">
      <h1 className="admin-page-title">الحجوزات</h1>

      <div className="admin-bookings-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'active'}
          className={`admin-bookings-tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => handleTabChange('active')}
        >
          الحجوزات
          <span className="admin-bookings-tab-count">{activeBookings.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'closed'}
          className={`admin-bookings-tab ${activeTab === 'closed' ? 'active' : ''}`}
          onClick={() => handleTabChange('closed')}
        >
          مغلق
          <span className="admin-bookings-tab-count">{closedBookings.length}</span>
        </button>
      </div>

      <div className="admin-bookings-filters">
        <div className="admin-bookings-filters-grid">
          <label className="admin-filter-field">
            <span>التاريخ</span>
            <input
              type="text"
              value={filters.date}
              onChange={(e) => setFilter('date', e.target.value)}
              placeholder="بحث..."
            />
          </label>
          <label className="admin-filter-field">
            <span>الوقت</span>
            <input
              type="text"
              value={filters.time}
              onChange={(e) => setFilter('time', e.target.value)}
              placeholder="بحث..."
            />
          </label>
          <label className="admin-filter-field">
            <span>العميل</span>
            <input
              type="text"
              value={filters.guest}
              onChange={(e) => setFilter('guest', e.target.value)}
              placeholder="بحث..."
            />
          </label>
          <label className="admin-filter-field">
            <span>الجوال</span>
            <input
              type="text"
              value={filters.phone}
              onChange={(e) => setFilter('phone', e.target.value)}
              placeholder="بحث..."
            />
          </label>
          <label className="admin-filter-field">
            <span>الخدمة</span>
            <input
              type="text"
              value={filters.service}
              onChange={(e) => setFilter('service', e.target.value)}
              placeholder="بحث..."
            />
          </label>
          <label className="admin-filter-field">
            <span>السعر</span>
            <input
              type="text"
              value={filters.price}
              onChange={(e) => setFilter('price', e.target.value)}
              placeholder="بحث..."
            />
          </label>
          <label className="admin-filter-field">
            <span>الحالة</span>
            <select
              value={filters.status}
              onChange={(e) => setFilter('status', e.target.value)}
              disabled={activeTab === 'closed'}
            >
              <option value="">الكل</option>
              {statusFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            className="admin-bookings-clear-filters"
            onClick={clearFilters}
          >
            مسح الفلاتر
          </button>
        )}
      </div>

      {visibleBookings.length === 0 ? (
        <div className="admin-empty">
          {hasActiveFilters
            ? 'لا توجد نتائج مطابقة للفلاتر'
            : activeTab === 'closed'
              ? 'لا توجد حجوزات مغلقة'
              : 'لا توجد حجوزات'}
        </div>
      ) : (
        <div className="admin-bookings-table-wrap">
          <table className="admin-bookings-table">
            <thead>
              <tr>
                <th>التاريخ</th>
                <th>الوقت</th>
                <th>العميل</th>
                <th>الجوال</th>
                <th>الخدمة</th>
                <th>السعر</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {visibleBookings.map((b) => {
                const statusInfo = getStatusInfo(getBookingStatus(b));
                return (
                  <tr
                    key={b.id}
                    className={`booking-row booking-row-${getBookingStatus(b)}`}
                  >
                    <td>{formatDate(b.booking_date)}</td>
                    <td>{b.slot_time}</td>
                    <td>
                      <strong>{b.guest_name}</strong>
                      {b.guest_email && (
                        <a href={`mailto:${b.guest_email}`} className="admin-email-link">
                          <FaEnvelope size={12} />
                        </a>
                      )}
                    </td>
                    <td>
                      {b.guest_phone && (
                        <a href={`tel:${b.guest_phone}`} className="admin-phone-link">
                          <FaPhone size={12} /> {b.guest_phone}
                        </a>
                      )}
                    </td>
                    <td>{b.service_header || b.service_name || '-'}</td>
                    <td>{b.price ? `${b.price} ريال` : '-'}</td>
                    <td>
                      <select
                        value={getBookingStatus(b)}
                        onChange={(e) => updateStatus(b.id, e.target.value)}
                        disabled={updating === b.id}
                        className="admin-status-select"
                        style={{
                          backgroundColor: statusInfo.color + '20',
                          color: statusInfo.color,
                          borderColor: statusInfo.color,
                        }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
